/**
 * views/PilotDashboard.tsx — /dashboard/missions
 * ──────────────────────────────────────────────
 * Pilot mission selection dashboard.
 *
 * Shows all PENDING + ACTIVE missions assigned to the pilot in a card grid.
 * "Start Mission" stores the selected mission ID in sessionStorage and
 * navigates to /dashboard/flight (PilotInterface), which reads it on mount
 * to pre-select the mission and drop the pilot straight into the upload flow.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, MapPin, Calendar, ClipboardList } from 'lucide-react';

import { useAuth }         from '@/contexts/AuthContext';
import { apiClient, ApiError } from '@/api/client';
import { InfiniumMark }   from '@/components/ui/InfiniumLogo';
import { Badge }          from '@/components/ui/badge';
import { Button }         from '@/components/ui/button';
import type { Mission, MissionStatus } from '@/types';

export const PILOT_MISSION_KEY = 'infinium_pilot_mission_id';

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_VARIANT: Record<MissionStatus, 'default' | 'success' | 'warning' | 'muted' | 'destructive'> = {
  PENDING:   'warning',
  ACTIVE:    'success',
  COMPLETED: 'default',
  CANCELLED: 'muted',
};

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

// ── Mission card ──────────────────────────────────────────────────────────────

function MissionCard({
  mission,
  onStart,
}: {
  mission: Mission;
  onStart: () => void;
}) {
  const canStart = mission.status === 'PENDING' || mission.status === 'ACTIVE';

  return (
    <div className="rounded-xl border bg-white p-5 shadow-sm flex flex-col gap-3 hover:shadow-md transition-shadow">
      {/* Status + title */}
      <div>
        <Badge variant={STATUS_VARIANT[mission.status]} className="mb-2">
          {mission.status}
        </Badge>
        <h3 className="font-semibold text-base leading-snug">
          {mission.title ?? `Mission ${mission.id.slice(0, 8)}…`}
        </h3>
        {mission.warehouse_name && (
          <p className="mt-0.5 text-sm text-muted-foreground">{mission.warehouse_name}</p>
        )}
      </div>

      {/* Allocated aisles */}
      {mission.allocated_aisles.length > 0 ? (
        <div>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            <MapPin className="h-3 w-3" /> Allocated Aisles
          </p>
          <div className="flex flex-wrap gap-1">
            {[...mission.allocated_aisles].sort().map(a => (
              <code key={a} className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">
                {a}
              </code>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground/70 italic flex items-center gap-1">
          <MapPin className="h-3 w-3" /> All aisles — no restriction
        </p>
      )}

      {/* Schedule window */}
      {(mission.scheduled_start || mission.scheduled_end) && (
        <div className="text-sm text-muted-foreground flex items-start gap-1.5">
          <Calendar className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            {fmtDate(mission.scheduled_start)}
            {mission.scheduled_end && <> → {fmtDate(mission.scheduled_end)}</>}
          </span>
        </div>
      )}

      {/* Briefing notes */}
      {mission.notes && (
        <p className="text-sm text-muted-foreground border-l-2 border-muted pl-2.5 italic leading-snug">
          {mission.notes}
        </p>
      )}

      {/* CTA */}
      <Button
        className="mt-auto w-full"
        variant={canStart ? 'default' : 'outline'}
        disabled={!canStart}
        onClick={onStart}
      >
        {canStart ? 'Start Mission →' : mission.status}
      </Button>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function PilotDashboard() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [missions, setMissions] = useState<Mission[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    apiClient.get<Mission[]>('/api/missions/mine')
      .then(data => { setMissions(data); setError(null); })
      .catch(err => setError(err instanceof ApiError ? err.message : 'Failed to load missions.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const startMission = (mission: Mission) => {
    sessionStorage.setItem(PILOT_MISSION_KEY, mission.id);
    navigate('/dashboard/flight');
  };

  return (
    <div className="pilot-page">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <header className="pilot-header">
        <div className="pilot-header-left">
          <InfiniumMark variant="dark" height={34} />
          <h1 className="pilot-header-title">My Missions</h1>
        </div>
        <div className="pilot-header-right">
          <span className="pilot-user">{user?.name || user?.email}</span>
          <button className="btn btn--ghost btn--sm" onClick={signOut}>Sign out</button>
        </div>
      </header>

      <div className="pilot-body">
        <div className="space-y-5">
          {/* Sub-header */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {loading
                ? 'Loading missions…'
                : `${missions.length} mission${missions.length !== 1 ? 's' : ''} assigned to you`}
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1.5 px-2 text-xs"
              onClick={load}
              disabled={loading}
            >
              <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}

          {/* Empty state */}
          {!loading && !error && missions.length === 0 && (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed bg-white py-16 text-center">
              <ClipboardList className="mx-auto mb-3 h-10 w-10 text-muted-foreground/40" />
              <p className="text-sm font-medium">No missions assigned yet</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Your manager will assign missions before your flight session.
              </p>
            </div>
          )}

          {/* Mission grid */}
          {missions.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {missions.map(m => (
                <MissionCard
                  key={m.id}
                  mission={m}
                  onStart={() => startMission(m)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
